<template>
  <a-modal
    title="创建配送任务"
    :width="760"
    :visible="visible"
    :confirmLoading="saving"
    :maskClosable="false"
    @ok="handleOk"
    @cancel="handleClose"
    cancelText="取消"
    okText="创建任务"
    style="top:60px;">
    <a-spin :spinning="loading">
      <a-form>
        <a-form-item :labelCol="{ span: 4 }" :wrapperCol="{ span: 18 }" label="配送员" required>
          <a-select v-model="courierUserId" placeholder="请选择配送员（本租户员工）">
            <a-select-option v-for="c in couriers" :key="c.id" :value="c.id">{{ c.username }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :labelCol="{ span: 4 }" :wrapperCol="{ span: 18 }" label="出库单" required
                     extra="仅可选择已出库（收单流程、订单状态=已出库）且未挂在未完成任务上的出库单">
          <a-select v-model="selectedBillIds" mode="multiple" placeholder="请选择要配送的出库单（可多选）"
                    @change="handleBillsChange">
            <a-select-option v-for="b in bills" :key="b.xsckHeadId" :value="b.xsckHeadId">
              {{ b.xsckNo }}（{{ b.customerName || '客户' }}）
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item v-if="stops.length > 0" :labelCol="{ span: 4 }" :wrapperCol="{ span: 19 }" label="配送点"
                     extra="地址默认取客户档案，可补录修改；通过上移/下移调整配送顺序">
          <div class="stop-list">
            <div v-for="(stop, index) in stops" :key="stop.xsckHeadId" class="stop-row">
              <div class="stop-seq">{{ index + 1 }}</div>
              <div class="stop-main">
                <div class="stop-head">
                  <span class="stop-customer">{{ stop.customerName || '客户' }}</span>
                  <span class="stop-no">出库单 {{ stop.xsckNo }}</span>
                </div>
                <div class="stop-addr">
                  <a-input v-model="stop.address" placeholder="配送地址（缺失请补录）" size="small" />
                </div>
              </div>
              <div class="stop-ops">
                <a-button size="small" icon="arrow-up" :disabled="index === 0" @click="moveStop(index, -1)" />
                <a-button size="small" icon="arrow-down" :disabled="index === stops.length - 1" @click="moveStop(index, 1)" />
              </div>
            </div>
          </div>
        </a-form-item>
        <a-form-item v-if="stops.length > 0" :labelCol="{ span: 4 }" :wrapperCol="{ span: 18 }" label="路线方案">
          <template v-if="plans.length > 0">
            <a-radio-group v-model="selectedRouteMode">
              <a-radio v-for="p in plans" :key="p.mode" :value="p.mode" class="plan-radio">
                {{ p.modeName }}：{{ p.summary }}
              </a-radio>
            </a-radio-group>
            <div class="plan-tip">选定方案将存入任务并作为下次默认方案；也可不选择，直接手工排序配送点</div>
          </template>
          <template v-else>
            <a-button icon="compass" :loading="planning" :disabled="stops.length < 2" @click="handlePlanRoute">规划路线</a-button>
            <span v-if="planTip" class="plan-tip-inline">{{ planTip }}</span>
            <div class="plan-tip">未选择高德方案时按手工排序配送</div>
          </template>
        </a-form-item>
      </a-form>
    </a-spin>
  </a-modal>
</template>
<script>
import { getDeliveryTaskOptions, planDeliveryRoute, addDeliveryTask } from '@/api/plugin/orderReceive'

export default {
  name: 'DeliveryTaskCreateModal',
  data() {
    return {
      visible: false,
      loading: false,
      saving: false,
      planning: false,
      couriers: [],
      bills: [],
      billMap: {},
      courierUserId: undefined,
      selectedBillIds: [],
      stops: [],
      plans: [],
      selectedRouteMode: undefined,
      planTip: '',
      lastRouteMode: 'manual'
    }
  },
  methods: {
    open() {
      this.visible = true
      this.loading = true
      this.courierUserId = undefined
      this.selectedBillIds = []
      this.stops = []
      this.plans = []
      this.selectedRouteMode = undefined
      this.planTip = ''
      getDeliveryTaskOptions().then(res => {
        if (res.success) {
          const data = res.data || {}
          this.couriers = data.couriers || []
          this.bills = data.bills || []
          this.billMap = {}
          this.bills.forEach(b => { this.billMap[b.xsckHeadId] = b })
          this.lastRouteMode = data.lastRouteMode || 'manual'
        } else {
          this.$message.warning(res.message)
        }
      }).finally(() => {
        this.loading = false
      })
    },
    handleClose() {
      this.visible = false
    },
    /** 选中出库单 → 生成配送点（保持选择顺序），地址默认客户档案 */
    handleBillsChange(ids) {
      this.plans = []
      this.selectedRouteMode = undefined
      this.planTip = ''
      this.stops = ids.map(id => {
        const b = this.billMap[id] || {}
        return {
          xsckHeadId: id,
          xsckNo: b.xsckNo,
          customerName: b.customerName,
          address: b.address || '',
          longitude: null,
          latitude: null
        }
      })
    },
    moveStop(index, step) {
      const target = index + step
      const tmp = this.stops[target]
      this.$set(this.stops, target, this.stops[index])
      this.$set(this.stops, index, tmp)
    },
    /** 高德驾车路线规划（多方案），key 未配置时降级手工排序 */
    handlePlanRoute() {
      this.planning = true
      this.planTip = ''
      planDeliveryRoute({ stops: this.stops }).then(res => {
        if (!res.success) {
          this.$message.warning(res.message)
          return
        }
        const data = res.data || {}
        if (!data.enabled) {
          this.planTip = data.message || '高德Key未配置，请手工排序配送点'
          return
        }
        // 地理编码补齐的坐标回填（随创建落库）
        if (data.geocoded && data.geocoded.length === this.stops.length) {
          this.stops.forEach((stop, i) => {
            stop.longitude = data.geocoded[i].longitude
            stop.latitude = data.geocoded[i].latitude
          })
        }
        this.plans = data.plans || []
        if (this.plans.length === 0) {
          this.planTip = data.message || '配送点不足，无需规划'
          return
        }
        // 默认选中上次方案（不在候选中则选第一个）
        const match = this.plans.find(p => p.mode === this.lastRouteMode)
        this.selectedRouteMode = match ? match.mode : this.plans[0].mode
      }).finally(() => {
        this.planning = false
      })
    },
    handleOk() {
      if (!this.courierUserId) {
        this.$message.warning('请选择配送员')
        return
      }
      if (this.stops.length === 0) {
        this.$message.warning('请选择出库单')
        return
      }
      if (this.stops.some(s => !s.address || !s.address.trim())) {
        this.$message.warning('存在配送点缺少地址，请补录后再创建')
        return
      }
      const selectedPlan = this.plans.find(p => p.mode === this.selectedRouteMode)
      const routeMode = selectedPlan ? selectedPlan.mode : 'manual'
      const routeSummary = selectedPlan ? selectedPlan.summary : ''
      this.saving = true
      addDeliveryTask({
        courierUserId: this.courierUserId,
        routeMode: routeMode,
        routeSummary: routeSummary,
        stops: this.stops.map(s => ({
          xsckHeadId: s.xsckHeadId,
          address: s.address,
          longitude: s.longitude,
          latitude: s.latitude
        }))
      }).then(res => {
        if (res.success) {
          this.$message.success('配送任务 ' + (res.data && res.data.taskNo) + ' 已创建')
          this.visible = false
          this.$emit('ok')
        } else {
          this.$message.warning(res.message)
        }
      }).finally(() => {
        this.saving = false
      })
    }
  }
}
</script>
<style scoped>
.stop-list {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 8px;
  max-height: 300px;
  overflow-y: auto;
}
.stop-row {
  display: flex;
  align-items: center;
  padding: 6px;
  border-bottom: 1px solid #f0f0f0;
}
.stop-row:last-child {
  border-bottom: none;
}
.stop-seq {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #1890ff;
  color: #fff;
  text-align: center;
  line-height: 26px;
  flex-shrink: 0;
  margin-right: 10px;
}
.stop-main {
  flex: 1;
}
.stop-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}
.stop-customer {
  font-weight: 600;
}
.stop-no {
  color: #888;
  font-size: 12px;
}
.stop-ops {
  margin-left: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.plan-radio {
  display: block;
  margin-bottom: 6px;
}
.plan-tip {
  color: #999;
  font-size: 12px;
  margin-top: 6px;
}
.plan-tip-inline {
  color: #faad14;
  margin-left: 8px;
  font-size: 13px;
}
</style>
